import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, money } from "@/lib/api";
import { Amount } from "@/components/shared/Amount";
import { RadioDropdown } from "@/components/shared/RadioDropdown";
import { StatusBadge } from "@/components/shared/Badges";
import { OnboardingChecklist } from "@/components/dash/OnboardingChecklist";
import { useContent } from "@/context/ContentContext";
import { fmtDateShort } from "@/components/shared/DashKit";
import { linkExample } from "@/lib/linkExample";
import { speedLabel, refillWord, noRefillWord } from "@/lib/labels";
import { detectCustomComments } from "@/lib/customComments";
import { toast } from "sonner";
import { Loader2, Wallet, Megaphone, X, ArrowRight, ShoppingBag, RefreshCw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const QLABEL = { economy: "Économie", standard: "Standard", premium: "Premium" };
const firstName = (name = "") => name.trim().split(/\s+/)[0] || "";
const DROW = "flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4";
const DVAL = "min-w-0 break-all font-medium sm:max-w-[65%] sm:text-right";


export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useContent();
  const ctx = useOutletContext();
  const nav = useNavigate();
  const [platformsMap, setPlatformsMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [catServices, setCatServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [balance, setBalance] = useState(ctx?.balance ?? 0);
  const [loading, setLoading] = useState(true);
  const [balLoaded, setBalLoaded] = useState(false);
  const [banner, setBanner] = useState(null);

  const [catKey, setCatKey] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [platformKey, setPlatformKey] = useState("");
  const [target, setTarget] = useState("");
  const [quantity, setQuantity] = useState("");
  const [comments, setComments] = useState("");
  const [placing, setPlacing] = useState(false);
  const [loadingSvc, setLoadingSvc] = useState(false);
  const [cap, setCap] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/platforms"), api.get("/categories"), api.get("/orders?limit=5"), api.get("/wallet"), api.get("/notifications")])
      .then(([p, c, o, w, n]) => {
        const pm = {}; p.data.forEach((x) => { pm[x.id] = x.name; });
        setPlatformsMap(pm); setCategories(c.data); setOrders(o.data.items); setBalance(w.data.balance); setBalLoaded(true);
        const newest = (n.data || [])[0];
        const dismissed = localStorage.getItem("twp_banner_dismissed");
        if (newest && dismissed !== newest.id) setBanner({ id: newest.id, title: newest.title, message: newest.message });
        else if (!newest && dismissed !== "welcome") setBanner({ id: "welcome", title: t("client.dash.welcome_title", "Bienvenue sur The World Promotions"), message: t("client.dash.welcome_msg", "Vos commandes sont livrées automatiquement. Ajoutez des fonds pour démarrer.") });
      })
      .finally(() => setLoading(false));
  }, []);

  const platformOptions = useMemo(() => {
    const withCats = new Set(categories.map((c) => c.platform_id));
    return Object.entries(platformsMap)
      .filter(([id]) => withCats.has(id))
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [categories, platformsMap]);

  const catOptions = useMemo(() => categories
    .filter((c) => c.platform_id === platformKey)
    .map((c) => ({ value: c.id, label: c.name, platform_id: c.platform_id }))
    .sort((a, b) => { const aa = a.label.startsWith("Autre"), bb = b.label.startsWith("Autre"); if (aa !== bb) return aa ? 1 : -1; return a.label.localeCompare(b.label, "fr"); }),
    [categories, platformKey]);

  const loadCatServices = async (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return;
    setLoadingSvc(true);
    try {
      const { data } = await api.get(`/services?platform_id=${cat.platform_id}&category_id=${categoryId}&sort=price_low&limit=200`);
      setCatServices(data.items);
    } catch { setCatServices([]); } finally { setLoadingSvc(false); }
  };

  const svc = catServices.find((s) => s.id === serviceId) || null;
  const isCC = detectCustomComments(svc);
  const commentLines = comments.split("\n").map((l) => l.trim()).filter(Boolean);
  const qty = isCC ? commentLines.length : (Number(quantity) || 0);
  const price = svc ? (svc.price_per_1000 * qty) / 1000 : 0;
  const after = balance - price;

  useEffect(() => {
    if (!svc || qty < svc.min_quantity || qty > svc.max_quantity) { setCap(null); return; }
    const t = setTimeout(() => {
      api.post("/orders/capacity-check", { service_id: svc.id, quantity: qty })
        .then((r) => setCap(r.data)).catch(() => setCap(null));
    }, 400);
    return () => clearTimeout(t);
  }, [serviceId, qty]); // eslint-disable-line

  const valid = svc && qty >= svc.min_quantity && qty <= svc.max_quantity && target.trim().length > 2 && after >= 0 && (!cap || cap.available);

  const svcOptions = catServices.map((s) => ({
    value: s.id, label: s.name,
    sub: `${QLABEL[s.quality_level] || "Standard"}${s.speed_description ? " · " + speedLabel(t, s.speed_description) : ""} · ${s.refill_available ? refillWord(t) : noRefillWord(t)} · ${s.min_quantity.toLocaleString("fr-FR")}–${s.max_quantity.toLocaleString("fr-FR")}`,
    right: `${money(s.price_per_1000)}/1k`,
  }));

  const submit = async () => {
    setPlacing(true);
    try {
      const payload = { service_id: svc.id, target: target.trim(), quantity: qty };
      if (isCC) payload.comments = commentLines.join("\n");
      const { data } = await api.post("/orders", payload);
      toast.success(t("client.toast.submitted", "Commande soumise avec succès !"));
      ctx?.reloadTop && ctx.reloadTop();
      nav(`/dashboard/orders/${data.id}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPlacing(false); }
  };

  const cleanComments = () => {
    const seen = new Set(); const out = [];
    for (const l of comments.split("\n")) { const v = l.trim(); if (v && !seen.has(v)) { seen.add(v); out.push(v); } }
    setComments(out.join("\n"));
  };

  const handleCommentsFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const txt = String(reader.result || ""); setComments((p) => (p.trim() ? p.replace(/\n+$/, "") + "\n" : "") + txt); };
    reader.readAsText(file); e.target.value = "";
  };

  const dismissBanner = () => { localStorage.setItem("twp_banner_dismissed", banner.id); setBanner(null); };

  const [confirm, setConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const doReorder = async () => {
    if (!confirm) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", { service_id: confirm.service_id, target: confirm.target, quantity: confirm.quantity });
      toast.success(t("client.toast.reordered", "Commande relancée !"));
      ctx?.reloadTop && ctx.reloadTop();
      setConfirm(null);
      nav(`/dashboard/orders/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 lg:max-w-5xl">
      <div data-testid="home-greeting">
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("client.dash.hello", "Bonjour")} {firstName(user?.name)},</h1>
        <p className="mt-1 text-base text-muted-foreground">{t("client.dash.balance_is", "votre solde est de")} {balLoaded
          ? <span data-testid="home-balance" className="font-display font-bold text-accent"><Amount value={balance} /></span>
          : <span className="inline-block h-4 w-16 animate-pulse rounded bg-secondary align-middle" />}</p>
      </div>

      <OnboardingChecklist
        ready={!loading}
        balance={balance}
        serviceSelected={!!serviceId}
        linkAdded={target.trim().length > 2}
        qtyValid={!!(svc && qty >= svc.min_quantity && qty <= svc.max_quantity)}
        ordersCount={orders.length}
      />

      {banner && (
        <div data-testid="announcement-banner" className="flex items-start gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4">
          <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{banner.title}</div>
            <div className="text-sm text-muted-foreground">{banner.message}</div>
          </div>
          <button data-testid="announcement-close" onClick={dismissBanner} className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">{t("client.dash.new_order", "Nouvelle Commande")}</h2>
          <Link to="/dashboard/add-funds" data-testid="add-funds-btn"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-accent px-3.5 py-2 text-xs font-semibold text-white transition-transform hover:scale-105 sm:text-sm">
            <Wallet className="h-4 w-4" /> {t("client.nav.add_funds", "Ajouter des fonds")}
          </Link>
        </div>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("client.dash.platform", "Plateforme")}</label>
              <RadioDropdown testid="order-platform" value={platformKey} placeholder={t("client.dash.choose_platform", "Choisir une plateforme")}
                options={platformOptions} onChange={(v) => { setPlatformKey(v); setCatKey(""); setServiceId(""); setQuantity(""); setComments(""); setCatServices([]); }} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("client.dash.category", "Catégorie")}</label>
              <RadioDropdown testid="order-category" value={catKey} disabled={!platformKey}
                placeholder={!platformKey ? t("client.dash.select_platform_first", "Sélectionnez d'abord une plateforme") : t("client.dash.choose_category", "Choisir une catégorie")}
                options={catOptions} onChange={(v) => { setCatKey(v); setServiceId(""); setQuantity(""); setComments(""); setCatServices([]); loadCatServices(v); }} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("client.dash.service", "Service")}</label>
              <RadioDropdown testid="order-service" value={serviceId}
                placeholder={!catKey ? t("client.dash.select_cat_first", "Sélectionnez d'abord une catégorie") : loadingSvc ? t("client.dash.loading_services", "Chargement des services...") : t("client.dash.choose_service", "Choisir un service")}
                disabled={!catKey || loadingSvc} options={svcOptions}
                onChange={(v) => { setServiceId(v); setComments(""); const s = catServices.find((x) => x.id === v); setQuantity(s ? s.min_quantity : ""); }} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("client.dash.link", "Lien")}</label>
              <input data-testid="order-link" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={linkExample(svc?.name)}
                className="w-full rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent/40" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("client.dash.quantity", "Quantité")}</label>
                <input type="number" data-testid="order-quantity" value={isCC ? qty : quantity} onChange={(e) => setQuantity(e.target.value)} disabled={!svc || isCC} readOnly={isCC}
                  placeholder={svc ? `${svc.min_quantity}–${svc.max_quantity}` : "—"}
                  title={isCC ? t("client.dash.qty_auto", "Quantité calculée automatiquement (1 commentaire par ligne)") : undefined}
                  className="w-full rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-50" />
                {isCC && svc && <p className="mt-1 text-xs text-muted-foreground">{t("client.dash.qty_auto", "Quantité calculée automatiquement (1 commentaire par ligne)")}</p>}
                {svc && <p className="mt-1 text-xs text-muted-foreground">{svc.min_quantity.toLocaleString("fr-FR")} – {svc.max_quantity.toLocaleString("fr-FR")}</p>}
                {cap && !cap.available && <p data-testid="capacity-msg" className="mt-1 text-xs font-medium text-red-500">{t("client.dash.capacity_unavail", "Cette quantité n'est pas disponible actuellement. Maximum disponible : ")}{(cap.max_available || 0).toLocaleString("fr-FR")}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("client.dash.total_price", "Prix total")}</label>
                <div data-testid="order-price-total" className="flex items-center rounded-xl border border-border bg-secondary/40 px-4 py-3 font-display text-lg font-bold"><Amount value={price} /></div>
                {svc && after < 0 && <p className="mt-1 text-xs text-destructive" data-testid="order-insufficient">{t("client.dash.insufficient", "Solde insuffisant")}</p>}
              </div>
            </div>
            {isCC && svc && (
              <div data-testid="home-comments-block">
                <label className="mb-1.5 block text-sm font-medium">{t("client.no.comments_label", "Commentaires (1 par ligne)")} <span className="text-muted-foreground">({svc.min_quantity.toLocaleString()} – {svc.max_quantity.toLocaleString()})</span></label>
                <textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="home-comments" rows={5}
                  placeholder={t("client.no.comments_placeholder", "Collez vos commentaires, un par ligne…")}
                  className="w-full resize-y rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent/40" />
                <div data-testid="home-comments-counter" className={`mt-1 text-xs ${qty < svc.min_quantity || qty > svc.max_quantity ? "text-red-600" : "text-muted-foreground"}`}>
                  {qty.toLocaleString()} {t("client.no.comments_counter", "commentaire(s)")}
                </div>
                {comments.length > 0 && (qty < svc.min_quantity || qty > svc.max_quantity) && (
                  <div data-testid="home-comments-error" className="mt-1 text-xs text-red-600">
                    {t("client.no.comments_range", "Le nombre de commentaires doit être entre {min} et {max}.").replace("{min}", svc.min_quantity.toLocaleString()).replace("{max}", svc.max_quantity.toLocaleString())}
                  </div>
                )}
                {comments.trim().length > 0 && (
                  <button type="button" data-testid="home-comments-clean" onClick={cleanComments}
                    className="mt-2 inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                    {t("client.no.comments_clean", "Nettoyer (lignes vides + doublons)")}
                  </button>
                )}
                <label data-testid="home-comments-import" className="ml-2 mt-2 inline-flex cursor-pointer items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                  <input type="file" accept=".txt,text/plain" className="hidden" onChange={handleCommentsFile} />
                  {t("client.no.comments_import", "Importer un .txt")}
                </label>
              </div>
            )}
            <button data-testid="submit-order-btn" disabled={!valid || placing} onClick={submit}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">
              {placing && <Loader2 className="h-4 w-4 animate-spin" />} {t("client.dash.submit", "Soumettre la commande")}
            </button>
          </div>
        )}
      </div>

      {svc && (
        <div data-testid="service-description-block" className="rounded-2xl border border-border bg-card p-5 sm:p-6">
          <h2 data-testid="service-detail-title" className="mb-1 font-display text-base font-bold text-foreground">{svc.name}</h2>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("client.dash.desc", "Description")}</h3>
          <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
            {svc.description ? (
              <p data-testid="service-admin-description" className="whitespace-pre-line break-words">{svc.description}</p>
            ) : (
              <p className="italic text-muted-foreground/70">{t("client.dash.no_desc", "Aucune description disponible.")}</p>
            )}
            {svc.provider_instructions && (
              <div className="border-t border-border pt-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("client.no.instructions", "Instructions")}</div>
                <p data-testid="service-provider-description" className="whitespace-pre-line break-words">{svc.provider_instructions}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold">{t("client.dash.recent", "Commandes récentes")}</h2>
          <Link to="/dashboard/orders" className="flex items-center gap-1 text-xs text-accent hover:underline">{t("client.dash.see_all", "Tout voir")} <ArrowRight className="h-3 w-3" /></Link>
        </div>
        {orders.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
            <ShoppingBag className="h-6 w-6" /> {t("client.dash.no_orders", "Aucune commande pour l'instant.")}
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {orders.map((o) => (
              <div key={o.id} data-testid={`recent-order-${o.id}`} className="flex items-center justify-between gap-3 py-3">
                <Link to={`/dashboard/orders/${o.id}`} className="flex min-w-0 flex-1 items-center justify-between gap-3 transition-opacity hover:opacity-80">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{o.service_name}</div>
                    <div className="text-xs text-muted-foreground">#{o.order_number} · {fmtDateShort(o.created_at)}</div>
                  </div>
                  <span className="font-display text-sm font-bold"><Amount value={o.price} /></span>
                </Link>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge status={o.status} />
                  <button onClick={() => setConfirm(o)} data-testid={`home-reorder-${o.id}`} title={t("client.reorder", "Recommander")}
                    className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent/25">
                    <RefreshCw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t("client.reorder", "Recommander")}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && !submitting && setConfirm(null)}>
        <AlertDialogContent data-testid="reorder-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("client.reorder.confirm_title", "Confirmer la nouvelle commande")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("client.reorder.confirm_desc", "Le montant sera déduit de votre solde. Vérifiez les détails avant de confirmer.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm && (
            <dl className="space-y-2.5 rounded-xl border border-border bg-secondary/40 p-4 text-sm" data-testid="reorder-details">
              <div className={DROW}><dt className="shrink-0 text-muted-foreground">{t("client.field.service", "Service")}</dt><dd className={DVAL}>{confirm.service_name}</dd></div>
              <div className={DROW}><dt className="shrink-0 text-muted-foreground">{t("client.field.target_link", "Lien cible")}</dt><dd className={DVAL}>{confirm.target}</dd></div>
              <div className={DROW}><dt className="shrink-0 text-muted-foreground">{t("client.field.quantity", "Quantité")}</dt><dd className={DVAL}>{confirm.quantity.toLocaleString()}</dd></div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2.5"><dt className="font-medium">{t("client.field.total_price", "Prix total")}</dt><dd className="font-display text-base font-bold text-accent"><Amount value={confirm.price} /></dd></div>
            </dl>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reorder-cancel-btn" disabled={submitting}>{t("client.cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction data-testid="reorder-confirm-btn" onClick={(e) => { e.preventDefault(); doReorder(); }} disabled={submitting}>
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("client.processing", "Traitement...")}</> : t("client.confirm", "Confirmer")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
